import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactOwnershipType, ContactUserProfile } from '@ui/shared/models';
import {
  ContextMenuComponent,
  ContextMenuItemComponent,
  ContextMenuTooltipPlacementEnum,
  DataTableCellComponent,
  DataTableCellDirective,
  DataTableComponent,
  DateTimePipe,
  FullNamePipe,
  HumanizeBooleanPipe,
  ListControlsComponent,
  ModalComponent,
  ModalContentComponent,
  ModalService
} from '@ui/legacy-lib';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import {
  getConnectedProfiles,
  getConnectedProfilesActionState,
  getConnectedProfilesPagination
} from 'landlord/main/+state/internal-list/internal-list.selectors';
import { LoadConnectedProfiles } from 'landlord/main/+state';
import { Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { AsyncPipe, NgClass } from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'app-member-linked-profiles-modal',
  templateUrl: './member-linked-profiles-modal.component.html',
  styleUrls: ['./member-linked-profiles-modal.component.scss'],
  imports: [
    NgClass,
    SvgIconComponent,
    AsyncPipe,
    DateTimePipe,
    FullNamePipe,
    HumanizeBooleanPipe,
    TranslateModule,
    ModalComponent,
    ModalContentComponent,
    ListControlsComponent,
    DataTableCellDirective,
    ContextMenuComponent,
    ContextMenuItemComponent,
    DataTableComponent,
    DataTableCellComponent
  ]
})
export class MemberLinkedProfilesModalComponent implements OnInit {
  private ngbActiveModal = inject(NgbActiveModal);
  private modalService = inject(ModalService);
  store = inject(Store);

  readonly contactId = input<string>(undefined);
  readonly isAdmin = input<boolean>(undefined);
  readonly ownershipType = input<ContactOwnershipType>(undefined);
  public connectedProfiles = signal<ContactUserProfile[]>(undefined);
  public isLoading = true;

  isAllResidents = computed(() =>
    this.connectedProfiles()
      .filter(profile => !profile.mainProfile)
      .every(profile => profile.resident)
  );
  public columns = [
    { name: 'name', label: 'NAME_L', noWrap: true },
    { name: 'dateOfBirth', label: 'general.birthdate_l', noWrap: true },
    {
      name: 'membershipNumber',
      label: 'general.membership_number_l',
      noWrap: true
    },
    { name: 'email', label: 'EMAIL_L', noWrap: true },
    { name: 'context-menu' }
  ];
  public pageSize = 10;
  public page = 1;
  public totalElements$: Observable<number>;

  protected readonly ContextMenuTooltipPlacementEnum =
    ContextMenuTooltipPlacementEnum;

  ngOnInit() {
    this.loadConnectedProfiles();
    this.store
      .select(getConnectedProfilesActionState)
      .pipe(
        filter(state => state?.done && !state?.pending),
        tap(() => (this.isLoading = false)),
        switchMap(() => this.store.select(getConnectedProfiles)),
        untilDestroyed(this)
      )
      .subscribe(data => this.connectedProfiles.set(data));

    this.totalElements$ = this.store
      .select(getConnectedProfilesPagination)
      .pipe(map(pagination => pagination?.totalElements));
  }

  public closeModal() {
    this.ngbActiveModal.dismiss();
  }

  public handleMarkAsMainProfileAction(markProfileIdAsMain: string) {
    this.ngbActiveModal.close({ markProfileIdAsMain });
  }

  public handleSeparateProfileAction(separateProfileId: string) {
    this.modalService
      .openConfirmation({
        data: {
          titleMessage:
            'contact_details_linked_profile_modal.separate_profile_title_l',
          message:
            'contact_details_linked_profile_modal.separate_profile_text_l',
          okButtonMessage: 'general.yes_l',
          cancelButtonMessage: 'general.no_l'
        }
      })
      .onClose()
      .subscribe(() => this.ngbActiveModal.close({ separateProfileId }));
  }

  public onPageChange(page: number) {
    this.page = page;
    this.loadConnectedProfiles();
  }

  public onSizeChange(pageSize: number) {
    this.pageSize = pageSize;
    this.loadConnectedProfiles();
  }

  private loadConnectedProfiles() {
    this.store.dispatch(
      new LoadConnectedProfiles(this.contactId(), this.pageSize, this.page)
    );
  }

  protected readonly ContactOwnershipType = ContactOwnershipType;
}
